import adminApi from './adminApi';

// ========== DTOs ==========
export interface SystemAdminDTO {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  status: string;
  isSuperAdmin: boolean;
  createdBy?: number;
  createdByName?: string;
  createdAt?: string;
  lastLoginAt?: string;
  assignedCourses?: CourseAssignmentDTO[];
}

export interface CourseAssignmentDTO {
  courseId: number;
  courseName: string;
  courseType: string;
  assignedAt: string;
  assignedBy?: number;
  assignedByName?: string;
  status: string;
}

export interface CreateSystemAdminRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  isSuperAdmin?: boolean;
}

export interface AssignCourseRequest {
  courseIds: number[];
}

// ========== API Functions ==========

/**
 * Get all system admins (super admin only)
 */
export const getAllSystemAdmins = async (): Promise<SystemAdminDTO[]> => {
  const response = await adminApi.get('/api/admin/system-admins');
  return response.data.data;
};

/**
 * Create a new system admin (super admin only)
 */
export const createSystemAdmin = async (
  request: CreateSystemAdminRequest
): Promise<SystemAdminDTO> => {
  const response = await adminApi.post('/api/admin/system-admins', request);
  return response.data.data;
};

/**
 * Update a system admin (super admin only)
 */
export const updateSystemAdmin = async (
  adminId: number,
  request: CreateSystemAdminRequest
): Promise<SystemAdminDTO> => {
  const response = await adminApi.put(`/api/admin/system-admins/${adminId}`, request);
  return response.data.data;
};

/**
 * Deactivate a system admin (super admin only)
 */
export const deactivateSystemAdmin = async (adminId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/system-admins/${adminId}`);
};

/**
 * Get courses assigned to an admin
 */
export const getAssignedCourses = async (
  adminId: number
): Promise<CourseAssignmentDTO[]> => {
  const response = await adminApi.get(`/api/admin/system-admins/${adminId}/courses`);
  return response.data.data;
};

/**
 * Assign courses to an admin (super admin only)
 */
export const assignCoursesToAdmin = async (
  adminId: number,
  request: AssignCourseRequest
): Promise<void> => {
  await adminApi.post(`/api/admin/system-admins/${adminId}/assign-courses`, request);
};

/**
 * Remove admin from a course (super admin only)
 */
export const removeCourseAssignment = async (
  adminId: number,
  courseId: number
): Promise<void> => {
  await adminApi.delete(`/api/admin/system-admins/${adminId}/courses/${courseId}`);
};

/**
 * Move admin from one course to another (super admin only)
 */
export const moveCourseAssignment = async (
  adminId: number,
  fromCourseId: number,
  toCourseId: number
): Promise<void> => {
  await adminApi.put(`/api/admin/system-admins/${adminId}/move-course`, null, {
    params: { fromCourseId, toCourseId },
  });
};

